import React, { useState } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { FaHome, FaProjectDiagram, FaTasks, FaBars, FaSignOutAlt, FaPlus, FaCalendarAlt } from 'react-icons/fa';
import TeamMember from './TeamMember';
import TeamMemberProjects from './TeamMemberProjects';
import CreateTask from './CreateTask';
import MyTasks from './MyTasks';
import TaskCalendar from './TaskCalendar';
// import API_URL from '../config';
import { disconnectWebSocket } from '../websocket';
import NotificationBell from "./NotificationBell";
import NotificationDialog from "./NotificationDialog";
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import { clearStoredAuth, getLoginPath } from '../utils/authSession';

const TeamMemberView = ({ user, setUser }) => {
  const { t } = useLanguage();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const sidebarIdentityInitial = 'TM';

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(prev => !prev);
  };
  const toggleResponsiveSidebar = () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      toggleSidebar();
      return;
    }

    toggleMobileSidebar();
  };
  const navLinkClass = ({ isActive }) =>
    `shell-nav-link ${isActive ? 'shell-nav-link-active' : ''} ${!isSidebarOpen ? 'lg:justify-center' : ''}`;

  const handleLogout = () => {
    const companyId = user?.company?.id || 1;
    disconnectWebSocket();
    clearStoredAuth();
    setUser(null);
    navigate(getLoginPath({ companyId }));
  };

  return (
    <div className="flex min-h-screen bg-background font-sans relative overflow-x-hidden">
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div 
            className="overlay-backdrop z-30 lg:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`shell-sidebar transition-all duration-300 fixed top-0 left-0 h-full z-40 flex flex-col
        w-64 ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 ${isSidebarOpen ? 'lg:w-80' : 'lg:w-20'}`}>
        <div className={`border-b border-border px-3 py-4 sm:px-4 ${!isSidebarOpen ? 'lg:px-3' : ''}`}>
          <div className={`relative flex items-start justify-between gap-3 ${!isSidebarOpen ? 'lg:justify-center' : ''}`}>
            <div className={`flex min-w-0 items-center gap-3 ${!isSidebarOpen ? 'lg:hidden' : ''}`}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-btn">
                <span className="text-sm font-semibold sm:text-base">{sidebarIdentityInitial}</span>
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-text sm:text-lg">
                  {user.company ? user.company.name : t('common.noCompany')}
                </p>
                <p className="truncate text-xs text-secondary-text sm:text-sm">{user.email}</p>
              </div>
            </div>
            <div className={`hidden h-10 w-10 items-center justify-center rounded-full bg-primary text-white shadow-btn ${!isSidebarOpen ? 'lg:flex' : ''}`}>
              <span className="text-sm font-semibold">{sidebarIdentityInitial}</span>
            </div>
            <div className={`${!isSidebarOpen ? 'lg:absolute lg:right-3 lg:top-0' : ''}`}>
              {/* Desktop Toggle */}
              <button onClick={toggleSidebar} className="hidden rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none lg:block">
                <FaBars size={16} className="text-base sm:text-lg md:text-xl" />
              </button>
              {/* Mobile Close */}
              <button onClick={toggleMobileSidebar} className="ml-auto rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none lg:hidden">
                <FaBars size={16} className="text-base sm:text-lg" />
              </button>
            </div>
          </div>
        </div>
        <nav className="flex justify-between items-center mt-8 sm:mt-3 md:mt-4">
          <ul className='flex justify-between items-center flex-col w-full'>
            <li className="w-full">
              <NavLink end to="/team-member" className={navLinkClass}>
                <FaHome className={`mr-2 sm:mr-3 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.dashboard')}</span>
              </NavLink>
            </li>
            <li className="w-full">
              <NavLink to="/team-member/projects" className={navLinkClass}>
                <FaProjectDiagram className={`mr-2 sm:mr-3 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.projects')}</span>
              </NavLink>
            </li>
            <li className="w-full">
              <NavLink to="/team-member/create-task" className={navLinkClass}>
                <FaPlus className={`mr-2 sm:mr-3 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.createTask')}</span>
              </NavLink>
            </li>
            <li className="w-full">
              <NavLink to="/team-member/my-tasks" className={navLinkClass}>
                <FaTasks className={`mr-2 sm:mr-3 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.myTasks')}</span>
              </NavLink>
            </li>
            <li className="w-full">
              <NavLink to="/team-member/calendar" className={navLinkClass}>
                <FaCalendarAlt className={`mr-2 sm:mr-3 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.calendar')}</span>
              </NavLink>
            </li>
            <li className={`w-full shell-nav-link ${!isSidebarOpen ? 'lg:justify-center' : ''}`} onClick={handleLogout}>
              <div className={`flex items-center w-full ${!isSidebarOpen ? 'lg:justify-center' : ''}`}>
                <FaSignOutAlt className={`mr-2 sm:mr-3 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.logout')}</span>
              </div>
            </li>
          </ul>
        </nav>
      </div>
      <div className={`min-w-0 flex-1 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-80' : 'lg:ml-20'}`}>
        <div className={`shell-topbar fixed top-0 right-0 z-30 flex items-center justify-between p-2 sm:p-3 md:p-4 transition-all duration-300 left-0 ${isSidebarOpen ? 'lg:left-80' : 'lg:left-20'}`}>
          <div className="flex items-center">
            {/* Mobile Toggle */}
            <button
              type="button"
              onClick={toggleResponsiveSidebar}
              className="mr-3 rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none"
              aria-label="Toggle sidebar"
              aria-expanded={isMobileSidebarOpen || isSidebarOpen}
            >
                <FaBars size={20} />
            </button>
            <span className="hidden text-text text-lg font-semibold truncate sm:block sm:text-xl">
                {user.company ? user.company.name : t('common.noCompany')}
            </span>
          </div>
          <div className="ml-3 flex items-center space-x-2 sm:space-x-2 md:space-x-3">
            <div>
              {/* Top right bell */}
              <div className="flex items-center justify-end gap-3">
                <LanguageToggle />
                <NotificationBell user={user} onAllNotificationsClick={() => setDialogOpen(true)} />
              </div>

              {/* Manager module main content goes here */}

              {/* Full notifications dialog */}
              <NotificationDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
              />
            </div>
            <button onClick={handleLogout} className="secondary-btn hidden items-center space-x-1 sm:inline-flex !px-3 !py-2 text-sm sm:text-base">
              <FaSignOutAlt className="text-base sm:text-lg" />
              <span>{t('common.logout')}</span>
            </button>
          </div>
        </div>
        <div className="mt-16 p-4 sm:p-5 md:p-6 overflow-x-auto">
          <div className="max-w-5xl sm:max-w-6xl md:max-w-7xl mx-auto">
            <Routes>
              <Route path="/" element={<TeamMember user={user} />} />
              <Route path="projects" element={<TeamMemberProjects user={user} />} />
              <Route path="create-task" element={<CreateTask user={user} setIsSidebarOpen={setIsSidebarOpen} />} />
              <Route path="my-tasks" element={<MyTasks user={user} />} />
              <Route path="calendar" element={<TaskCalendar user={user} />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberView;
